import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
} from '@mui/material';
import { Science as ScienceIcon, Link as LinkIcon } from '@mui/icons-material';
import apiClient from '@services/apiClient';
import * as labResultsService from '@services/labResultsService';
import * as clinicLabIntegrationsService from '@services/clinicLabIntegrationsService';
import { useAuth } from '@contexts/AuthContext';

interface UnmatchedOrder {
  id: string;
  patient_id: string | null;
  visit_id: string | null;
  clinic_id: string;
  status: string;
  document_url: string | null;
  external_ref: string | null;
  created_at: string;
  metadata?: Record<string, unknown>;
}

const ReceptionUnmatchedLabResults: React.FC = () => {
  const { user } = useAuth();
  const clinicId = (user as { clinicId?: string })?.clinicId;
  const [list, setList] = useState<UnmatchedOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<UnmatchedOrder | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState<{ id: string; name: string; owner?: string }[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [visits, setVisits] = useState<{ id: string; visit_date: string; visit_type: string }[]>([]);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadUnmatched = async () => {
    if (!clinicId) return;
    setLoading(true);
    setError('');
    try {
      const res = await clinicLabIntegrationsService.listUnmatchedLabResults(clinicId, 50, 0);
      setList((res.data || []) as UnmatchedOrder[]);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Eroare la încărcare');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnmatched();
  }, [clinicId]);

  const openAssign = (order: UnmatchedOrder) => {
    setSelectedOrder(order);
    setPatientSearch('');
    setPatients([]);
    setSelectedPatientId(null);
    setVisits([]);
    setSelectedVisitId(null);
    setAssignDialogOpen(true);
  };

  const searchPatients = async () => {
    if (!clinicId || !patientSearch.trim()) return;
    try {
      const res = await apiClient.get('/patients', { params: { clinicId, search: patientSearch, limit: 20 } });
      const items = (res.data?.data?.patients || res.data?.data || res.data || []) as any[];
      setPatients(
        items.map((p: any) => ({
          id: p.id,
          name: p.name || `${p.id}`,
          owner: p.ownerFirstName || p.owner ? `${p.owner?.firstName || ''} ${p.owner?.lastName || ''}`.trim() : undefined,
        }))
      );
    } catch (_) {
      setPatients([]);
    }
  };

  useEffect(() => {
    if (!selectedPatientId) {
      setVisits([]);
      setSelectedVisitId(null);
      return;
    }
    (async () => {
      try {
        const res = await apiClient.get(`/visits/patient/${selectedPatientId}`, { params: { clinicId } });
        const items = (res.data?.data || res.data || []) as any[];
        setVisits(
          items.map((v: any) => ({
            id: v.id,
            visit_date: v.visit_date || v.visitDate,
            visit_type: v.visit_type || v.visitType || 'consultation',
          }))
        );
      } catch (_) {
        setVisits([]);
      }
    })();
  }, [selectedPatientId, clinicId]);

  const handleAssign = async () => {
    if (!selectedOrder || !selectedPatientId) return;
    setSaving(true);
    try {
      await labResultsService.assignLabResultToPatient(selectedOrder.id, selectedPatientId, selectedVisitId || undefined);
      setAssignDialogOpen(false);
      loadUnmatched();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Eroare la atașare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScienceIcon /> Rezultate laborator nemapate
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rezultate primite prin email care nu au putut fi asociate automat unui pacient/vizită. Atașează manual la pacient și vizită.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : list.length === 0 ? (
            <Alert severity="info">Nu există rezultate nemapate.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Ref. externă</TableCell>
                    <TableCell>Metadate</TableCell>
                    <TableCell align="right">Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                      <TableCell>{row.external_ref || '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {row.metadata ? JSON.stringify(row.metadata).slice(0, 60) + '…' : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<LinkIcon />} onClick={() => openAssign(row)}>
                          Atașează
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Atașează rezultat la pacient și vizită</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Caută pacient (nume)"
              value={patientSearch}
              onChange={(e) => setPatientSearch(e.target.value)}
              onBlur={searchPatients}
              fullWidth
            />
            <Autocomplete
              options={patients}
              getOptionLabel={(o) => (o.owner ? `${o.name} – ${o.owner}` : o.name)}
              value={patients.find((p) => p.id === selectedPatientId) || null}
              onChange={(_, v) => setSelectedPatientId(v?.id ?? null)}
              renderInput={(params) => <TextField {...params} label="Pacient" />}
            />
            <Autocomplete
              options={visits}
              getOptionLabel={(v) => `${new Date(v.visit_date).toLocaleDateString('ro-RO')} – ${v.visit_type}`}
              value={visits.find((v) => v.id === selectedVisitId) || null}
              onChange={(_, v) => setSelectedVisitId(v?.id ?? null)}
              renderInput={(params) => <TextField {...params} label="Vizită (opțional)" />}
              disabled={!selectedPatientId}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Anulare</Button>
          <Button variant="contained" onClick={handleAssign} disabled={!selectedPatientId || saving}>
            {saving ? 'Se salvează…' : 'Atașează'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceptionUnmatchedLabResults;
